import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Building2, Loader2, UserPlus, Mail } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { queryKeys } from '@/lib/query-keys';
import { useEnterpriseCapabilities } from '@/hooks/useEnterpriseCapabilities';
import { usePartnerMemberList } from '@/hooks/useEnterpriseSeatUsage';
import {
  useSendActivationLink,
  isNotEligibleError,
} from '@/hooks/useProvisionEnterpriseMember';
import { useToast } from '@/hooks/use-toast';
import { SeatUsageStat } from '@/components/enterprise/SeatUsageStat';
import { AddMemberDialog } from '@/components/enterprise/AddMemberDialog';
import { EnterpriseEmulateModal } from '@/components/admin/enterprise/EnterpriseEmulateModal';
import { MemberCapabilityControls } from '@/components/admin/enterprise/MemberCapabilityControls';
import type { PartnerMemberCapabilities } from '@/lib/enterprise-capabilities';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Row shape from enterprise_emulation_member_list (#619). The RPC is
 * capability-aware and only returns emulatable members, so every row here is
 * emulatable by construction. Used for non-admin `emulate`-capability holders,
 * who are not authorized to call the admin-gated partner_member_list.
 */
interface EmulationMember {
  buildit_user_id: string;
  email: string;
  status: string;
  last_sign_in_at: string | null;
  sso_last_login_at: string | null;
}

/** Unified row the members table renders, normalized from either source. */
interface MemberRow {
  user_id: string;
  email: string;
  last_sign_in_at: string | null;
  is_emulatable: boolean;
  /** Manually provisioned (no active SSO identity yet). Only meaningful in the
   *  admin source; the emulate source only returns emulatable (SSO) members. */
  is_manual: boolean;
}

interface EmulationTarget {
  userId: string;
  email: string;
}

/**
 * /settings/enterprise — enterprise member list + emulate launcher (spec §6.5).
 * The page self-gates on useEnterpriseCapabilities (defense in depth for
 * direct-URL access). The emulate launcher + member list are shown to users
 * with the `emulate` capability OR partner-admins (canEmulateOfPartner);
 * capability MANAGEMENT stays partner-admin-only (isAdminOfPartner). The real
 * authorization boundary is server-side: enterprise_emulation_member_list is
 * capability-aware, the capability-list RPC raises 42501 for non-admins, and
 * the start edge fn recomputes the 5-clause predicate.
 *
 * Reuses the PartnerIdentityList table primitives (shadcn Table) for a
 * consistent enterprise look.
 */
export function EnterpriseSettings(): JSX.Element {
  const { partners, isLoading, hasAnyEnterpriseCapability } = useEnterpriseCapabilities();
  const { toast } = useToast();
  const sendActivationLink = useSendActivationLink();

  // Default to the first partner; the switcher only renders for >1.
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [emulateTarget, setEmulateTarget] = useState<EmulationTarget | null>(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const partnerId = selectedPartnerId ?? partners[0]?.partner_id ?? '';

  // Only full partner admins / super_admins may manage capabilities. The
  // capability-list RPC is admin-gated (raises 42501 for non-admins), so a
  // delegated (non-admin) viewer must NOT fire it (avoids an error toast).
  const isAdminOfPartner =
    partners.find((p) => p.partner_id === partnerId)?.is_admin ?? false;

  // Delegated `emulate`-capability holders (non-admins) are authorized to use
  // the emulate launcher even though they cannot manage the partner (#619).
  // Capability MANAGEMENT and seat management stay isAdminOfPartner-only.
  const canEmulateOfPartner =
    isAdminOfPartner ||
    (partners.find((p) => p.partner_id === partnerId)?.capabilities?.includes('emulate') ??
      false);

  // Member list has two sources, chosen by privilege:
  //  * Admins/super_admins read the FULL roster (manual + SSO) via
  //    partner_member_list so they can manage seats. Each row carries
  //    is_emulatable (canonical is_emulatable_member: active SSO identity AND
  //    not a partner/super admin) for per-row Emulate gating. The RPC is
  //    admin-gated (caller_is_partner_admin_or_super → 403 for non-admins), so
  //    fire it only for admins.
  //  * Non-admin `emulate`-capability holders read the capability-aware
  //    enterprise_emulation_member_list (every returned row is emulatable).
  //    partner_member_list would 403 for them, so they must use this source.
  const {
    data: adminMembers = [],
    isLoading: adminLoading,
    error: adminError,
    refetch: refetchAdmin,
  } = usePartnerMemberList(isAdminOfPartner ? partnerId : '');

  const {
    data: emulateMembers = [],
    isLoading: emulateLoading,
    error: emulateError,
    refetch: refetchEmulate,
  } = useQuery({
    queryKey: queryKeys.enterprisePartners.emulationMembers(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('enterprise_emulation_member_list', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as EmulationMember[];
    },
    // Only the non-admin emulate-capability path uses this source; admins read
    // the broader partner_member_list above.
    enabled: !!partnerId && canEmulateOfPartner && !isAdminOfPartner,
  });

  // Normalize whichever source is active into the unified row shape.
  const members = useMemo<MemberRow[]>(() => {
    if (isAdminOfPartner) {
      return adminMembers.map((m) => ({
        user_id: m.user_id,
        email: m.email,
        last_sign_in_at: m.last_sign_in_at,
        is_emulatable: m.is_emulatable,
        is_manual: m.is_manual,
      }));
    }
    return emulateMembers.map((m) => ({
      user_id: m.buildit_user_id,
      email: m.email,
      last_sign_in_at: m.last_sign_in_at,
      is_emulatable: true, // RPC only returns emulatable members
      is_manual: false, // emulate source is SSO-backed by construction
    }));
  }, [isAdminOfPartner, adminMembers, emulateMembers]);

  const membersLoading = isAdminOfPartner ? adminLoading : emulateLoading;
  const membersError = isAdminOfPartner ? adminError : emulateError;
  const refetch = isAdminOfPartner ? refetchAdmin : refetchEmulate;

  // Per-member capability grants for the selected partner. Admin-gated RPC, so
  // only fire it when the caller is a full admin of this partner.
  const { data: capabilityRows = [] } = useQuery({
    queryKey: queryKeys.enterprisePartners.capabilityList(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('enterprise_partner_capability_list', {
        p_partner_id: partnerId,
      });
      if (error) {
        captureError(error as Error, { feature: 'enterprise-capability-list' });
        throw error;
      }
      return (data ?? []) as PartnerMemberCapabilities[];
    },
    enabled: !!partnerId && isAdminOfPartner,
  });

  const capMap = useMemo(() => {
    const map = new Map<string, PartnerMemberCapabilities>();
    for (const row of capabilityRows) {
      map.set(row.user_id, row);
    }
    return map;
  }, [capabilityRows]);

  const selectedPartner = useMemo(
    () => partners.find((p) => p.partner_id === partnerId) ?? null,
    [partners, partnerId],
  );

  // Admin action (#615): email a manually-added, non-admin member a first-login
  // link so they can sign in (and become emulatable). A `not_eligible` result is
  // an expected business outcome — surfaced to the admin, not a bug.
  const handleSendActivationLink = (userId: string) => {
    sendActivationLink.mutate(
      { partner_id: partnerId, user_id: userId },
      {
        onSuccess: ({ email }) => {
          toast({
            title: 'First-login link sent',
            description: `First-login link sent to ${email}.`,
          });
        },
        onError: (err) => {
          toast({
            variant: 'destructive',
            title: isNotEligibleError(err)
              ? "Couldn't send link"
              : 'Could not send first-login link',
            description: err instanceof Error ? err.message : 'Please try again.',
          });
        },
      },
    );
  };

  // --- Gating (after hooks, to satisfy rules-of-hooks) ---------------------
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!hasAnyEnterpriseCapability) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-foreground" />
            Members
          </h2>
          {canEmulateOfPartner ? (
            <p className="text-sm text-muted-foreground">
              Emulate a member of your enterprise to provide first-line support. Every
              session is fully audit-logged and the member sees a banner if they sign in
              while it&rsquo;s active.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Members of your enterprise and their workspaces.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {partners.length > 1 && (
            <div className="w-56">
              <Select
                value={partnerId}
                onValueChange={(v) => setSelectedPartnerId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select enterprise" />
                </SelectTrigger>
                <SelectContent>
                  {partners.map((p) => (
                    <SelectItem key={p.partner_id} value={p.partner_id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isAdminOfPartner && (
            <Button size="sm" onClick={() => setAddMemberOpen(true)}>
              <UserPlus className="mr-2 h-3.5 w-3.5" />
              Add member
            </Button>
          )}
        </div>
      </div>

      {isAdminOfPartner && <SeatUsageStat partnerId={partnerId} />}

      {!canEmulateOfPartner ? (
        <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
          You have monitoring access; member management is admin-only.
        </div>
      ) : membersLoading ? (
        <div className="space-y-2">
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-10 animate-pulse rounded bg-muted" />
        </div>
      ) : membersError ? (
        <div className="space-y-3 rounded border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <p>
            Failed to load members:{' '}
            {membersError instanceof Error ? membersError.message : 'Unknown error'}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void refetch();
            }}
          >
            Retry
          </Button>
        </div>
      ) : members.length === 0 ? (
        <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
          {isAdminOfPartner ? (
            <>
              No members
              {selectedPartner ? ` in ${selectedPartner.name}` : ''} yet. Add one
              above to get started.
            </>
          ) : (
            <>
              No members available to emulate
              {selectedPartner ? ` in ${selectedPartner.name}` : ''} yet.
            </>
          )}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Last login</TableHead>
              {isAdminOfPartner && <TableHead>Capabilities</TableHead>}
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m) => {
              const lastLogin = m.last_sign_in_at
                ? formatDistanceToNow(new Date(m.last_sign_in_at), {
                    addSuffix: true,
                  })
                : 'Never';
              return (
                <TableRow key={m.user_id}>
                  <TableCell className="text-sm">{m.email}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {lastLogin}
                  </TableCell>
                  {isAdminOfPartner && (
                    <TableCell>
                      <MemberCapabilityControls
                        partnerId={partnerId}
                        userId={m.user_id}
                        isAdmin={capMap.get(m.user_id)?.is_admin ?? false}
                        capabilities={
                          capMap.get(m.user_id)?.capabilities ?? []
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {(() => {
                      if (m.is_emulatable) {
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setEmulateTarget({
                                userId: m.user_id,
                                email: m.email,
                              })
                            }
                          >
                            <Building2 className="mr-2 h-3.5 w-3.5" />
                            Emulate
                          </Button>
                        );
                      }
                      // Manually-added, non-admin member who hasn't signed in yet:
                      // admins can email them a first-login link (#615). Once they
                      // sign in, they become emulatable.
                      const isManualNonAdmin =
                        isAdminOfPartner &&
                        m.is_manual &&
                        !(capMap.get(m.user_id)?.is_admin ?? false);
                      if (isManualNonAdmin) {
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={sendActivationLink.isPending}
                            onClick={() => handleSendActivationLink(m.user_id)}
                          >
                            <Mail className="mr-2 h-3.5 w-3.5" />
                            Send first-login link
                          </Button>
                        );
                      }
                      return (
                        <span className="text-xs text-muted-foreground">
                          SSO sign-in required to emulate
                        </span>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {emulateTarget && (
        <EnterpriseEmulateModal
          open={emulateTarget !== null}
          onOpenChange={(open) => {
            if (!open) setEmulateTarget(null);
          }}
          partnerId={partnerId}
          targetUserId={emulateTarget.userId}
          targetEmail={emulateTarget.email}
        />
      )}

      {isAdminOfPartner && (
        <AddMemberDialog
          partnerId={partnerId}
          open={addMemberOpen}
          onOpenChange={setAddMemberOpen}
        />
      )}
    </div>
  );
}
